import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaBell, FaSearch, FaGlobe, FaBars, FaChartBar, FaExternalLinkAlt, FaShare } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import './Topbar.css';

const Topbar = ({ onMenuClick }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [language, setLanguage] = useState('en');
  const [notificationCount, setNotificationCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  // Debug logging
  useEffect(() => {
    console.log('Topbar - User loaded:', user);
    console.log('Topbar - User ID (_id):', user?._id);
    console.log('Topbar - User ID (id):', user?.id);
  }, [user]);

  useEffect(() => {
    fetchNotificationCount();
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchNotificationCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchNotificationCount = async () => {
    try {
      const response = await api.get('/notifications/count');
      setNotificationCount(response.data.unreadCount || 0);
    } catch (error) {
      console.error('Failed to fetch notification count:', error);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await api.get('/notifications?limit=10');
      setNotifications(response.data.notifications || []);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  const handleNotificationClick = async () => {
    if (!showNotifications) {
      await fetchNotifications();
    }
    setShowNotifications(!showNotifications);
  };

  const handleNotificationItemClick = async (notification) => {
    try {
      // Mark as read
      await api.put(`/notifications/${notification._id}/read`);
      
      // Navigate to link if available
      if (notification.link) {
        navigate(notification.link);
      }
      
      setShowNotifications(false);
      fetchNotificationCount();
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.put('/notifications/read-all');
      fetchNotificationCount();
      fetchNotifications();
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'hi' : 'en');
  };

  const handleViewPublicProfile = () => {
    console.log('User object:', user);
    console.log('User ID (_id):', user?._id);
    console.log('User ID (id):', user?.id);
    
    const userId = user?._id || user?.id;
    
    if (userId) {
      window.open(`/public-profile/${userId}`, '_blank');
    } else {
      console.error('User ID not available. User object:', user);
      alert('Unable to load profile. Please refresh the page and try again.');
    }
  };

  const getNotificationIcon = (type) => {
    const icons = {
      order: '📦',
      product: '🛍️',
      support: '💬',
      finance: '💰',
      interest: '🎯',
      system: '🔔'
    };
    return icons[type] || '🔔';
  };

  const getTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  return (
    <div className="topbar">
      <button className="menu-btn" onClick={onMenuClick} aria-label="Toggle menu">
        <FaBars />
      </button>
      
      <div className="topbar-search">
        <FaSearch className="search-icon" onClick={() => { if (searchQuery.trim()) navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`); }} style={{ cursor: 'pointer' }} />
        <input 
          type="text" 
          placeholder="Search products, orders, or sellers..." 
          className="search-input"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && searchQuery.trim()) {
              navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
            }
          }}
        />
      </div>

      <div className="topbar-actions">
        <button className="topbar-btn language-btn" onClick={toggleLanguage}>
          <FaGlobe />
          <span className="btn-text">{language === 'en' ? 'English' : 'हिन्दी'}</span>
        </button>

        {(user?.userType === 'admin' || user?.userType === 'support') && (
          <button 
            className="topbar-btn monitoring-btn" 
            onClick={() => navigate('/monitoring')}
            title="Monitoring & Analytics Dashboard"
          >
            <FaChartBar />
            <span className="btn-text">Monitoring</span>
          </button>
        )}

        {(user?._id || user?.id) && (
          <button 
            className="topbar-btn public-profile-btn" 
            onClick={handleViewPublicProfile}
            title="View My Public Profile"
          >
            <FaExternalLinkAlt />
            <span className="btn-text">Public Profile</span>
          </button>
        )}
        
        <div className="notification-wrapper">
          <button className="topbar-btn" onClick={handleNotificationClick}>
            <FaBell />
            {notificationCount > 0 && (
              <span className="notification-badge">{notificationCount > 99 ? '99+' : notificationCount}</span>
            )}
          </button>

          {showNotifications && (
            <div className="notification-dropdown">
              <div className="notification-header">
                <h3>Notifications</h3>
                {notificationCount > 0 && (
                  <button className="mark-all-read" onClick={markAllAsRead}>
                    Mark all as read
                  </button>
                )}
              </div>
              <div className="notification-list">
                {notifications.length === 0 ? (
                  <div className="no-notifications">
                    <p>No notifications yet</p>
                  </div>
                ) : (
                  notifications.map((notification) => (
                    <div
                      key={notification._id}
                      className={`notification-item ${!notification.isRead ? 'unread' : ''}`}
                      onClick={() => handleNotificationItemClick(notification)}
                    >
                      <div className="notification-icon">
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="notification-content">
                        <div className="notification-title">{notification.title}</div>
                        <div className="notification-message">{notification.message}</div>
                        <div className="notification-time">{getTimeAgo(notification.createdAt)}</div>
                      </div>
                      {!notification.isRead && <div className="unread-dot"></div>}
                    </div>
                  ))
                )}
              </div>
              {notifications.length > 0 && (
                <div className="notification-footer">
                  <button onClick={() => navigate('/notifications')}>View all notifications</button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="topbar-profile" title={user?.email || 'Profile'}>
          {user?.profileImage ? (
            <img 
              src={user.profileImage} 
              alt="Profile" 
              className="profile-image"
              onError={(e) => {
                e.target.style.display = 'none';
                // Try to get initial from different user properties in order of preference
                const initial = 
                  user?.consumerProfile?.firstName?.[0]?.toUpperCase() ||
                  user?.businessName?.[0]?.toUpperCase() || 
                  user?.email?.[0]?.toUpperCase() || 
                  'U';
                e.target.parentElement.innerHTML = `<div class="profile-image default-avatar">${initial}</div>`;
              }}
            />
          ) : (
            <div className="profile-image default-avatar">
              {user?.consumerProfile?.firstName?.[0]?.toUpperCase() ||
               user?.businessName?.[0]?.toUpperCase() || 
               user?.email?.[0]?.toUpperCase() || 
               'U'}
            </div>
          )}
          <div className="topbar-profile-info">
            <div className="profile-name">{user?.businessName || user?.phone}</div>
            <div className="profile-email">{user?.email}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Topbar;
